<template>
  <div class="department-management">
    <div>
    <div class="content-container">
        <div class="header-section">
        <h1>Department Management</h1>
        <div class="action-buttons">
          <button
              v-if="$canAccess('DepartmentAdd')"
              class="btn btn-primary"
              @click="$router.push({ name: 'DepartmentAdd' })"
          >
            <i class="fas fa-plus-circle"></i> Add Department
          </button>
          <button
              class="btn btn-secondary"
              @click="$router.push({ name: 'nurses' })"
          >
            <i class="fas fa-arrow-left"></i> Back to Nurses
          </button>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Departments Table -->
      <div v-if="!loading && departments && departments.length > 0" class="department-table-container">
        <div class="table-filter mb-3">
          <input
              type="text"
              class="form-control"
              v-model="searchTerm"
              placeholder="Search departments..."
          >
        </div>

        <table class="table table-striped table-hover table-bordered">
        <thead>
            <tr>
              <th @click="sortBy('name')">
                Name 
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('code')">
                Code
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('description')">
                Description
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('created_at')">
                Created
                <i class="fas fa-sort"></i>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="department in paginatedDepartments" :key="department.id" :class="{'inactive-row': !department.is_active}">
              <td>{{ department.name }}</td>
              <td>{{ department.code || 'N/A' }}</td>
              <td>{{ department.description || 'No description provided' }}</td>
              <td>{{ $formatDateTime(department.created_at) }}</td>

              <td>
                <div class="btn-group">
                  <button
                      v-if="$canAccess('DepartmentEdit')"
                      class="btn btn-sm btn-outline-primary"
                    @click="editDepartment(department.id)"
                  >
                    Edit
                  </button>

                  <button
                      v-if="$canAccess('departmentView')"
                      class="btn btn-sm btn-outline-info"
                    @click="viewDepartmentDetails(department.id)"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination-container">
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="setPage(currentPage - 1)">Previous</button>
              </li>
              <li 
                v-for="page in totalPages" 
                :key="page" 
                class="page-item"
                :class="{ active: page === currentPage }"
              >
                <button class="page-link" @click="setPage(page)">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="setPage(currentPage + 1)">Next</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && (!departments || departments.length === 0)" class="empty-state">
        <div class="empty-state-content">
          <i class="fas fa-building fa-3x"></i>
          <h3>No Departments Found</h3>
          <p>There are no departments in the system yet. Get started by adding a new department.</p>
          <button
              v-if="$canAccess('departmentAdd')"
              class="btn btn-primary"
            @click="$router.push({ name: 'departmentAdd' })"
          >
            <i class="fas fa-plus-circle"></i> Add Department
          </button>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DepartmentList',
  data() {
    return {
      departments: [],
      loading: false,
      error: null,
      searchTerm: '',
      statusFilter: 'all',
      sortColumn: 'name',
      sortDirection: 'asc',
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    filteredDepartments() {
      if (!this.departments) return [];
      
      let filtered = [...this.departments];
      
      // Apply search filter
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        filtered = filtered.filter(department => 
          department.name.toLowerCase().includes(searchLower) ||
          (department.description && department.description.toLowerCase().includes(searchLower))
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        let valueA = a[this.sortColumn];
        let valueB = b[this.sortColumn];
        
        // Handle null values
        if (valueA === null) valueA = '';
        if (valueB === null) valueB = '';
        
        // String comparison
        if (typeof valueA === 'string') {
          valueA = valueA.toLowerCase();
          valueB = valueB.toLowerCase();
        }
        
        // Date comparison
        if (this.sortColumn === 'created_at') {
          valueA = new Date(valueA);
          valueB = new Date(valueB);
        }
        
        // Do the actual comparison
        if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      return filtered;
    },
    paginatedDepartments() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.filteredDepartments.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.filteredDepartments.length / this.itemsPerPage);
    }
  },
  methods: {
    async fetchDepartments() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await this.$api.get('/departments');
        this.departments = response.data.data;
      } catch (error) {
        console.error('Error fetching departments:', error);
        this.error = 'Failed to load departments. Please try again later.';
        this.departments = [];
      } finally {
        this.loading = false;
      }
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        // Toggle direction if already sorting by this column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Default to ascending for new column
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    setPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
   
    async toggleStatus(departmentId) {
      if (!confirm('Are you sure you want to change this department\'s status?')) return;
      
      try {
        await this.$api.patch(`/departments/${departmentId}/toggle`);
        const department = this.departments.find(d => d.id === departmentId);
        if (department) {
          department.is_active = !department.is_active;
          this.$toast.success(`Department ${department.is_active ? 'activated' : 'deactivated'} successfully`);
        }
      } catch (error) {
        console.error('Error toggling department status:', error);
        this.$toast.error('Failed to update department status. Please try again.');
      }
    },
    editDepartment(departmentId) {
      this.$router.push({
        name: 'DepartmentEdit',
        params: {id: departmentId}
      });
    },
    viewDepartmentDetails(departmentId) {
      this.$router.push({
        name: 'DepartmentView',
        params: { id: departmentId } 
      });
    }
  },
  mounted() {
    this.fetchDepartments();
  }
}
</script>

<style scoped>
.table td {
  text-align: left;
}

.department-management {
  padding: 20px;
}

.content-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  width: 100%;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.table-filter {
  display: flex;
  gap: 15px;
}

.table-filter input {
  max-width: 300px;
}

.btn-group {
  display: flex;
  gap: 5px;
}

.inactive-row {
  background-color: rgba(255, 0, 0, 0.05);
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.empty-state-content {
  text-align: center;
  max-width: 500px;
}

.empty-state-content i {
  color: #ccc;
  margin-bottom: 10px;
}

.empty-state-content h3 {
  margin-bottom: 10px;
}

.empty-state-content p {
  margin-bottom: 20px;
  color: #666;
}

th {
  cursor: pointer;
}

th i {
  margin-left: 5px;
  opacity: 0.5;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .table-filter {
    flex-direction: column;
  }
  
  .input-group {
    max-width: 100%;
  }
}
</style>